import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import SafeUrlAssembler from 'safe-url-assembler';
import { ApiEndpointService } from '../../core/services/api-endpoint.service';
import { Location } from '../../shared/models/location/location.model';

@Injectable()
export class ManageLocationsService {

    public constructor(private httpClient: HttpClient,
                       private apiEndpointService: ApiEndpointService,
    ) {
    }

    /**
     * Gets all locations.
     *
     * @returns {Observable<Location[]>}
     */
    public getLocations(): Observable<Location[]> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getLocations')).toString();
        return this.httpClient.get<Location[]>(endpoint);
    }

    /**
     * Gets all countries.
     *
     * @returns {Observable<any>}
     */
    public getCountries(): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getCarrierCountries'))
            .query({
                carriercode: 'UPS',
                isEU: false,
                selectiontype: 'address',
            })
            .toString();
        return this.httpClient.get<any>(endpoint);
    }

    /**
     * Gets a location based on the specified location ID.
     *
     * @param {string} locationId
     * @returns {Observable<Location>}
     */
    public getLocation(locationId: string): Observable<Location> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getLocation'))
            .param('locationId', locationId)
            .toString();
        return this.httpClient.get<Location>(endpoint);
    }

    /**
     * Checks default location.
     *
     * @param {string} locationId
     * @returns {Observable<boolean>}
     */
    public checkDefaultLocation(locationId: string): Observable<boolean> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('checkDefaultLocation'))
            .param('locationId', locationId)
            .toString();
        return this.httpClient.get<boolean>(endpoint);
    }

    /**
     * Saves default location.
     *
     * @param {string} contactId
     * @returns {Observable<any>}
     */
    public saveDefaultLocation(contactId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('saveDefaultLocation'))
            .param('contactId', contactId)
            .toString();
        return this.httpClient.post<any>(endpoint, {});
    }

    /**
     * Deletes a location based on the specified location ID.
     *
     * @param {string} locationId
     * @returns {Observable<any>}
     */
    public deleteLocation(locationId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('deleteLocation'))
            .param('locationId', locationId)
            .toString();
        return this.httpClient.delete(endpoint, { responseType: 'text' });
    }

    /**
     * Checks guest location.
     *
     * @param {string} locationId
     * @returns {Observable<boolean>}
     */
    public checkGuestLocation(locationId: string): Observable<boolean> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('checkGuestLocation'))
            .param('locationId', locationId)
            .toString();
        return this.httpClient.get<boolean>(endpoint);
    }

    /**
     * Saves location.
     *
     * @param {Location} location
     * @returns {Observable<any>}
     */
    public saveLocation(location: Location): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('saveLocation')).toString();
        return this.httpClient.post<any>(endpoint, location);
    }
}
